import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
    TableSortLabel,
    TablePagination,
    Chip,
    InputAdornment,
    TextField,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Assignment as ContractIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import { formatarMoeda, formatarData, formatarCPF } from '../../utils/formatadores.js';

export default function ListaContratos() {
    const navigate = useNavigate();
    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filters
    const [busca, setBusca] = useState(""); // TODO: implement backend search logic for general text if needed
    const [statusFilter, setStatusFilter] = useState("");

    // Pagination & Sort
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // TODO: Client side or server side sort? using client side for simpler mvp

    useEffect(() => {
        carregarContratos();
    }, [statusFilter]);

    const carregarContratos = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            // if (busca) params.busca = busca; // Backend need to support

            const response = await api.get('/contrato', { params });
            setContratos(response.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar contratos:', error);
            setError('Erro ao carregar contratos.');
        } finally {
            setLoading(false);
        }
    };

    const statusColors = {
        'ATIVO': 'success',
        'RASCUNHO': 'default',
        'AGUARDANDO_PAGAMENTO': 'warning',
        'BLOQUEADO_INADIMPLENCIA': 'error',
        'CANCELADO': 'error',
        'PENDENTE_INSTALACAO': 'info'
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Box sx={{ margin: '0 1.5% 1.5% 1.5%', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2, gap: 2 }}>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ContractIcon sx={{ color: '#20ac9c', fontSize: 32 }} />
                        Gestão de Contratos
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/contratos/novo')}
                        sx={{ backgroundColor: '#20ac9c' }}
                    >
                        <AddIcon /> Novo Contrato
                    </Button>
                </Box>

                {/* Filters */}
                <Box sx={{ p: 2, backgroundColor: 'background.paper', mb: 2, borderRadius: 1, display: 'flex', gap: 2 }}>
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="ATIVO">Ativo</MenuItem>
                        <MenuItem value="AGUARDANDO_PAGAMENTO">Aguardando Pagamento</MenuItem>
                        <MenuItem value="RASCUNHO">Rascunho</MenuItem>
                        <MenuItem value="CANCELADO">Cancelado</MenuItem>
                    </TextField>
                    {/* Future Search Field */}
                </Box>

                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Titular</TableCell>
                                <TableCell>Plano</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell>Vencimento</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center"><Preloader /></TableCell></TableRow>
                            ) : contratos.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center">Nenhum contrato encontrado</TableCell></TableRow>
                            ) : (
                                contratos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((contrato) => (
                                    <TableRow key={contrato.id_contrato_cartao} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {contrato.titular?.usuario?.st_nome || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                CPF: {formatarCPF(contrato.titular?.st_cpf)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{contrato.plano?.st_nome}</TableCell>
                                        <TableCell>{formatarMoeda(contrato.plano?.nu_valor_mensal_centavos)}</TableCell>
                                        <TableCell>Dia {contrato.nu_dia_vencimento}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={contrato.en_status?.replace('_', ' ')}
                                                color={statusColors[contrato.en_status] || 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Detalhes (Em Breve)">
                                                <IconButton size="small">
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={contratos.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Box>
        </Box>
    );
}
